import React, { useCallback, useEffect, useState } from 'react';

import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { useDevice } from '@bonnet/next/device';

import { BrowserStorage } from '@atc/browser-storage';
import { useFeatures } from '@atc/features';

import clientConfig from '@kbbsdk/config/clientConfig';
import { Header } from '@kbbsdk/global-sdk/navigation/web';

import { sendClick } from 'reaxl-analytics';
import { ErrorBoundary } from 'reaxl-molecules';
import { WalletNotification } from 'reaxl-wallet';

import sendAlertClickHandler from '@/utilities/sendAlertClickHandler';

import authDuck from '@/ducks/authDuck.js';
import birfDuck from '@/ducks/birfDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import kbbPageDataDuck from '@/ducks/kbbPageDataDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';

import ArgoThemeProvider from '@/containers/ArgoThemeProvider';

import LazyComponent from '@/components/LazyComponent';

const UpdateSavedInventoryContainer = dynamic(() => import('@/containers/UpdateSavedInventoryContainer'), {
    ssr: false,
});
const UpdateSavedSearchContainer = dynamic(() => import('@/containers/UpdateSavedSearchContainer'), { ssr: false });

const setUserApiHost = () => {

    const prodHosts = ['speedy.autotrader.com', 'www.autotrader.com', 'www.kbb.com'];

    const isProd = prodHosts.includes(globalThis.location?.hostname)
        || globalThis.location?.hostname.includes('awsacs.kbb.com')
        || globalThis.location?.hostname.includes('awsacs.autotrader.com');

    if (isProd) {
        return 'https://use1-user-api-master.awscsuser.kbb.com';
    }

    // NONPROD Url
    return 'https://use1-user-api-master.awscsusernp.kbb.com';
};

function LazyLoadedInvContainer() {

    const handleSaveInventoryUpdate = useCallback(
        (data) => {
            sendAlertClickHandler({
                data,
                sendClick,
            });
        },
        []
    );

    return (
        <LazyComponent fallback={null}>
            <ErrorBoundary>
                <UpdateSavedInventoryContainer onSaveInventoryUpdate={handleSaveInventoryUpdate} />
                <UpdateSavedSearchContainer onSaveSearchUpdate={handleSaveInventoryUpdate} />
            </ErrorBoundary>
        </LazyComponent>
    );
}

const AuthDuckUpdater = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        (async () => {
            if (typeof window !== 'undefined') {
                const { default: importAuthUtilityBundle } = await import('@/utilities/importAuthUtilityBundle');
                const { isUserSignedIn } = await importAuthUtilityBundle();
                const isLoggedIn = await isUserSignedIn();

                if (isLoggedIn) {
                    dispatch(authDuck.creators.setLoggedIn());
                }

                window.caiEventBus.on('auth.signin', () => {
                    dispatch(authDuck.creators.setLoggedIn());
                });

                window.caiEventBus.on('auth.signout', () => {
                    dispatch(authDuck.creators.setLoggedOut());
                });
            }
        })();

        return () => {
            window.caiEventBus.off('auth.signin');
            window.caiEventBus.off('auth.signout');
        };
    }, [dispatch]);

    return false;
};

export const shouldTriggerDealReminder = (dealDetails) => {
    // check that deal(s) is/are older than 24 hours
    const oneDay = 24 * 60 * 60 * 1000;
    const today = Date.now();

    if (!dealDetails || !Object.keys(dealDetails)?.length) {
        return false;
    }

    const dealIds = Object.keys(dealDetails);

    // check to see that user has not already been reminded to schedule test drive or submit credit app
    return dealIds.some((dealId) => {
        const deal = dealDetails[dealId];
        const testDriveNotReminded = !deal?.testDrive?.testDriveNotified;
        const creditAppNeedsReminder = !deal?.creditApp?.creditAppNotified
            && !deal?.creditApp?.creditAppSubmittedFlag
            && today - Date.parse(deal.createdDate) > oneDay;

        return testDriveNotReminded || creditAppNeedsReminder;
    });
};

// Helper function to handle valuation changes
export const handleValuationChanges = (hasNewTradeIn, isNewValuations, walletNotifCache, valuationHistory) => {
    if (hasNewTradeIn && isNewValuations) {
        walletNotifCache.setItem({ ...walletNotifCache.getItem(), valuationHistory, show: true });
    }
};

// Helper function to handle deal notification changes
export const handleDealNotificationChanges = (dealNotifications, walletNotifCache) => {
    if (_isEmpty(dealNotifications)) {
        return;
    }

    const totalCachedDealNotifs = walletNotifCache.getItem()?.dealNotifs;
    const dealNotifs = dealNotifications?.total;

    if (!totalCachedDealNotifs || dealNotifs > totalCachedDealNotifs) {
        walletNotifCache.setItem({ ...walletNotifCache.getItem(), dealNotifs, show: true });
    } else if (dealNotifs < totalCachedDealNotifs) {
        walletNotifCache.setItem({ ...walletNotifCache.getItem(), dealNotifs });
    }
};

// Helper function to check for new valuations
export const checkForNewValuations = (valuationHistory, cachedValuations) => {
    if (valuationHistory === null) {
        return false;
    }

    const hasLengthChange = valuationHistory?.length !== cachedValuations.length;
    const hasDateChange = valuationHistory?.[0]?.valuationDate !== cachedValuations[0]?.valuationDate;

    return hasLengthChange || hasDateChange;
};

// Helper function to calculate total notifications
export const calculateTotalNotifications = (hasNewTradeIn, dealNotifications, shouldShowDealReminder) => (hasNewTradeIn ? 1 : 0) + (dealNotifications?.total || 0) + (shouldShowDealReminder ? 1 : 0);

function KBBHeaderContent(props) {
    const {
        littleguy_webcomponent: [enableWebComponent],
        show_wallet_alert_notification: [showWalletAlertNotification],
    } = useFeatures(['littleguy_webcomponent', 'show_wallet_alert_notification']);

    const dispatch = useDispatch();

    const { navigation } = useSelector(kbbPageDataDuck.selectors.getDuckState);

    const pageInstanceId = useSelector(birfDuck.selectors.getPgInst);
    const [instanceId, setInstanceId] = useState(pageInstanceId);

    const walletNotifCache = new BrowserStorage('WALLET_NOTIF_POPOVER', { persist: true });
    const dealDetails = useSelector(dealsDuck.selectors.getTransformedDealDetails);
    const dealNotifications = useSelector(dealsDuck.selectors.getDealNotifications);

    const { hasNewTradeIn = false, valuationHistory } = useSelector(paymentsDuck.selectors.getTradeIn);

    const isClientRendered = typeof window !== 'undefined';
    const popoverContainerEl = isClientRendered && document.body;

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    if (pageInstanceId && pageInstanceId !== instanceId) {
        setInstanceId(pageInstanceId);
    }

    const handleWalletIconClick = (event) => {
        dispatch(
            paymentsDuck.creators.updateMyWallet({
                displayModal: true,
            })
        );
        walletNotifCache.setItem({ ...walletNotifCache.getItem(), show: false });
        sendClick('headerGeneralEventHandler', event, { linkContext: 'myWallet' });
    };

    // Extract notification logic
    const cachedValuations = walletNotifCache.getItem()?.valuations || [];
    const isNewValuations = checkForNewValuations(valuationHistory, cachedValuations);

    handleValuationChanges(hasNewTradeIn, isNewValuations, walletNotifCache, valuationHistory);
    handleDealNotificationChanges(dealNotifications, walletNotifCache);

    const shouldShowDealReminder = shouldTriggerDealReminder(dealDetails);
    if (shouldShowDealReminder) {
        walletNotifCache.setItem({ ...walletNotifCache.getItem(), show: true });
    }

    const handleWalletNotificationClick = (e) => {
        // Since ATC and KBB use same click tagging I added it in WalletHeaderPopover for convinient
        e.stopPropagation();
        dispatch(
            paymentsDuck.creators.updateMyWallet({
                displayModal: true,
                selectedPanelName: e.target.dataset.panel,
            })
        );
    };

    const hideNotification = () => {
        walletNotifCache.setItem({ ...walletNotifCache.getItem(), show: false });
    };

    const showNotification = showWalletAlertNotification && walletNotifCache.getItem()?.show;
    const totalNotifications = calculateTotalNotifications(hasNewTradeIn, dealNotifications, shouldShowDealReminder);
    const position = isXs ? 'bottom' : 'top';

    const walletNotification = showNotification && (
        createPortal(<WalletNotification
            onClick={handleWalletNotificationClick}
            onCloseClick={hideNotification}
            hasNewTradeIn={hasNewTradeIn}
            dealNotifications={dealNotifications}
            dealDetails={dealDetails}
            paymentsDuck={paymentsDuck}
            uiContext="toast"
            position={position}
        />, popoverContainerEl)
    );

    return (
        <Header
            useWebComponent={enableWebComponent}
            suppressZipCode
            onWalletIconClick={handleWalletIconClick}
            enableGtm
            withMyKbb
            navigation={navigation}
            hasNewTradeIn={hasNewTradeIn}
            totalWalletNotifs={totalNotifications}
            walletPopoverContainer={walletNotification}
            ignoreWebComponentJS={enableWebComponent}
            slimLittleGuy
            {...props}
        />
    );
}

function KBBHeader(props) {
    const pageInstanceId = useSelector(birfDuck.selectors.getPgInst);
    const [instanceId, setInstanceId] = useState(pageInstanceId);

    if (pageInstanceId && pageInstanceId !== instanceId) {
        setInstanceId(pageInstanceId);
    }

    // set the KBB userApiUrl
    const userApiHost = setUserApiHost();
    clientConfig.userApiUrl = userApiHost;

    return (
        <ArgoThemeProvider brand="kbb">
            <AuthDuckUpdater />
            <KBBHeaderContent {...props} />
            <LazyLoadedInvContainer />
        </ArgoThemeProvider>
    );
}

export default KBBHeader;
